import {
  ReadSchemaRequest,
  ReadSchemaResponse,
  SchemaServiceClient,
  SchemaSubType,
  SchemaType,
} from '@policyfly/protobuf/pfschema'
import { toSentenceCase } from '@policyfly/utils/string'
import { GrpcStatusCode } from '@protobuf-ts/grpcweb-transport'
import { RpcError } from '@protobuf-ts/runtime-rpc'

import { devtools } from '@/plugins/devtools/api'

import type { ApiVariableEndpoints, CreateEndpointParams } from '@/stores/api'
import type {
  SchemaApplication,
  SchemaAttachments,
  SchemaBinder,
  SchemaDefinition,
  SchemaIssue,
  SchemaQuote,
  SchemaQuoteReview,
  SchemaReview,
  SchemaSchedules,
  SchemaSettings,
  SchemaTranslations,
} from '@policyfly/protobuf/pfschema'

type SchemaKind = NonNullable<SchemaDefinition['definition']['oneofKind']>
type SchemaRequestSingle<T> = (opts: Pick<ReadSchemaRequest, 'slug' | 'version'>) => Promise<T>

/**
 * Extracts the schema from the response based on the provided `oneofKind`.
 *
 * If schema is missing throws an `RpcError` with `NOT_FOUND` status code.
 */
function extractSchema<T> (response: ReadSchemaResponse, oneofKind: SchemaKind): T {
  const definition = response.schema?.schema?.definition
  if (definition?.oneofKind !== oneofKind) {
    throw new RpcError('Missing schema definition', GrpcStatusCode[GrpcStatusCode.NOT_FOUND])
  }
  return definition[oneofKind as keyof typeof definition] as T
}

export interface SchemaApiEndpoints {
  /**
   * Loads an `application` schema for the specified request.
   */
  application: SchemaRequestSingle<SchemaApplication>
  /**
   * Loads an `attachments` schema for the specified request.
   */
  attachments: SchemaRequestSingle<SchemaAttachments>
  /**
   * Loads a `binder` schema for the specified request.
   */
  binder: SchemaRequestSingle<SchemaBinder>
  /**
   * Loads an `issue` schema for the specified request.
   */
  issue: SchemaRequestSingle<SchemaIssue>
  /**
   * Loads a `quote` schema for the specified request.
   */
  quote: SchemaRequestSingle<SchemaQuote>
  /**
   * Loads a `quoteReview` schema for the specified request.
   */
  quoteReview: SchemaRequestSingle<SchemaQuoteReview>
  /**
   * Loads a `review` schema for the specified request.
   */
  review: SchemaRequestSingle<SchemaReview>
  /**
   * Loads a `schedules` schema for the specified request.
   */
  schedules: SchemaRequestSingle<SchemaSchedules>
  /**
   * Loads a `settings` schema for the specified request.
   */
  settings: SchemaRequestSingle<SchemaSettings>
  /**
   * Loads a `translations` schema for the specified request.
   */
  translations: SchemaRequestSingle<SchemaTranslations>
}

export const createSchemaEndpoints = (params: CreateEndpointParams): ApiVariableEndpoints<SchemaApiEndpoints> => {
  const schemaServiceClient = new SchemaServiceClient(params.transport)

  function createSchemaRequestSingle<T> (type: SchemaType, kind: SchemaKind): SchemaRequestSingle<T> {
    return async (opts) => {
      const request = ReadSchemaRequest.create({
        ...opts,
        type,
        subType: SchemaSubType.SCHEMA_SUB_TYPE_INITIAL,
      })
      const { response } = await schemaServiceClient.read(request)

      devtools.logGrpc({
        description: `${toSentenceCase(kind)} Schema`,
        messages: [
          { type: ReadSchemaRequest, key: 'request', message: request },
          { type: ReadSchemaResponse, key: 'response', message: response },
        ],
      })

      return extractSchema(response, kind)
    }
  }

  const endpoints = {
    application: createSchemaRequestSingle<SchemaApplication>(SchemaType.SCHEMA_TYPE_APPLICATION, 'application'),
    attachments: createSchemaRequestSingle<SchemaAttachments>(SchemaType.SCHEMA_TYPE_ATTACHMENTS, 'attachments'),
    binder: createSchemaRequestSingle<SchemaBinder>(SchemaType.SCHEMA_TYPE_BINDER, 'binder'),
    issue: createSchemaRequestSingle<SchemaIssue>(SchemaType.SCHEMA_TYPE_ISSUE, 'issue'),
    quote: createSchemaRequestSingle<SchemaQuote>(SchemaType.SCHEMA_TYPE_QUOTE, 'quote'),
    quoteReview: createSchemaRequestSingle<SchemaQuoteReview>(SchemaType.SCHEMA_TYPE_QUOTE_REVIEW, 'quoteReview'),
    review: createSchemaRequestSingle<SchemaReview>(SchemaType.SCHEMA_TYPE_REVIEW, 'review'),
    schedules: createSchemaRequestSingle<SchemaSchedules>(SchemaType.SCHEMA_TYPE_SCHEDULES, 'schedules'),
    settings: createSchemaRequestSingle<SchemaSettings>(SchemaType.SCHEMA_TYPE_SETTINGS, 'settings'),
    translations: createSchemaRequestSingle<SchemaTranslations>(SchemaType.SCHEMA_TYPE_TRANSLATION, 'translations'),
  }

  return {
    django: endpoints,
    grpc: endpoints,
  }
}
